import axios from 'axios';
import {
    ProcessType,
    TaskProgressType,
    formatIsoTimestamp,
    getDisplayDateTimeParts,
} from '@quyva/common';
import { getUrl, getFastApiUrl } from './getUrl';
import { Config } from '../config/envConfig';

export const isExtractionTaskType = (taskType) => {
    if (!taskType) return false;
    return (
        taskType === ProcessType.EXTRACTION ||
        String(taskType).toLowerCase() === 'extraction'
    );
};

export const mapProgressToStatus = (progress) => {
    const progressLower =
        typeof progress === 'string' ? progress.toLowerCase() : '';
    // 'waiting' means the run is parked on this user supplying something and can be
    // acted on; 'paused' is the older generic value that carries no such promise.
    if (progressLower === 'waiting') return 'Waiting';
    if (progressLower === 'paused') return 'Paused';

    switch (progress) {
        case TaskProgressType.COMPLETED:
            return 'Success';
        case TaskProgressType.FAILED:
            return 'Fail';
        // A user-initiated cancel is a deliberate choice, not a failure.
        case TaskProgressType.CANCELLED:
            return 'Cancelled';
        case TaskProgressType.WARNING:
            return 'Warning';
        case TaskProgressType.PROCESSING:
        case TaskProgressType.STARTED:
        case TaskProgressType.CANCELLING:
        default:
            return 'Processing';
    }
};

export const parseOutputText = (rawOutput) => {
    if (!rawOutput) return null;

    try {
        if (typeof rawOutput === 'object') {
            return rawOutput;
        }
        return JSON.parse(rawOutput);
    } catch (e) {
        return {
            response:
                typeof rawOutput === 'string'
                    ? rawOutput
                    : 'Unable to parse output. Please try again.',
        };
    }
};

/**
 * @returns {Promise<Array|null>} the user's task history, or null if it could not be read.
 *   An empty array means "you have no tasks" and is a real answer; null means "no answer",
 *   and callers must not treat it as an empty history and blank what is on screen.
 */
export const getNotificationHistoryForUser = async (user) => {
    try {
        if (!user || !user.email || !user.token) {
            return null;
        }

        const dynamicUrl = getUrl(
            `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/getNotificationHistory?email=${user.email}`
        );

        const encodedToken = encodeURIComponent(user.token);
        const response = await axios.get(dynamicUrl, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: encodedToken,
            },
        });

        if (response && response.data?.notificationHistory) {
            // Filter out ALERT notifications - they should only appear in bell icon, not Tasks tab
            const filteredNotifications = response.data.notificationHistory.filter(
                (notification) => notification.process !== 'alert'
            );
            
            return filteredNotifications.map((notification) => {
                const rawPercentage =
                    typeof notification.percentage_completion === 'number'
                        ? notification.percentage_completion
                        : typeof notification.percentageCompletion === 'number'
                        ? notification.percentageCompletion
                        : null;

                // notification_history persists no percentage, so terminal
                // states fall back to a derived value. A WARNING (e.g. partially
                // crawled) is still a FINISHED task and must read 100% like
                // COMPLETED -- only genuine failures stay at 0. (Cancelled also
                // stays 0: it did not finish.)
                const percentageCompletion =
                    rawPercentage !== null
                        ? rawPercentage
                        : notification.progress === TaskProgressType.COMPLETED ||
                          notification.progress === TaskProgressType.WARNING
                        ? 100
                        : 0;

                return {
                    id: notification.id,
                    userMail: notification.user_mail,
                    repoId: notification.repo_id,
                    repoName: notification.repo_name,
                    processId:
                        notification.processID ||
                        notification.processId ||
                        notification.process_id,
                    process: notification.process,
                    progress: notification.progress,
                    percentageCompletion,
                    message: notification.message,
                    timeStamp: notification.timestamp,
                    creationTime: notification.creation_time,
                    output_text: notification.output_text,
                    output_files: notification.output_files,
                    templateName:
                        notification.templateName || notification.templatename,
                    addedFileNames:
                        notification.added_file_names ||
                        notification.addedFileNames ||
                        notification.fileNames,
                };
            });
        }

        // The field is missing entirely - a malformed response, not an empty history.
        return null;
    } catch (error) {
        console.warn('Failed to load task notifications:', error?.message);
        return null;
    }
};

/**
 * The user's runs that are parked waiting on them, keyed by process id.
 *
 * The task tab is fed by two tables: notification_history (via the Node history route and
 * the socket) supplies the rows, and suspended_scripts supplies what only exists while a
 * run is parked - its real status, what kind of input it needs, and the input's shape.
 * Read separately and merged by process_id, so the notification pipeline is untouched.
 *
 * @returns {Promise<Record<string, object>>} empty when nothing is waiting or on failure -
 *   losing the overlay must never cost the user their task list.
 */
export const getWaitingSuspensionsForUser = async (user) => {
    try {
        if (!user?.email) return {};

        const formData = new FormData();
        formData.append('user_id', String(user.email));

        const response = await axios.post(
            getFastApiUrl('/tool_utils/get_waiting_suspensions'),
            formData
        );

        const suspensions = Array.isArray(response.data?.suspensions)
            ? response.data.suspensions
            : [];

        return suspensions.reduce((acc, suspension) => {
            if (suspension?.process_id) {
                acc[String(suspension.process_id)] = suspension;
            }
            return acc;
        }, {});
    } catch (error) {
        console.warn('Failed to load waiting suspensions:', error?.message);
        return {};
    }
};

/**
 * Overlay waiting-suspension facts onto task rows, matched by process id.
 *
 * The suspension's status wins over the row's: it is the more specific truth ('waiting' on
 * the user, vs. notification_history's generic 'paused'). Rows with no suspension are
 * returned untouched, and any stale overlay on them is cleared - a resolved suspension is
 * deleted server-side, so the row must stop offering to act on it.
 */
export const applyWaitingSuspensions = (rows, suspensionsByProcessId) => {
    const suspensions = suspensionsByProcessId || {};

    return (rows || []).map((row) => {
        const suspension = row?.processId ? suspensions[String(row.processId)] : null;

        if (!suspension) {
            if (!row?.suspensionId && !row?.requiredInput) return row;
            return {
                ...row,
                suspensionId: null,
                suspendEventType: null,
                requiredInput: null,
                waitingMessage: null,
            };
        }

        return {
            ...row,
            status: mapProgressToStatus(suspension.status),
            progress: suspension.status,
            suspensionId: suspension.suspension_id ?? null,
            suspendEventType: suspension.suspend_event_type ?? null,
            requiredInput: suspension.required_input ?? null,
            waitingMessage: suspension.waiting_message ?? null,
        };
    });
};

export const mapNotificationToTaskRow = (notification) => {
    // Always a string. notification_history.process_id is a BigInteger, so the history API
    // hands it over as a JSON number, while the socket and suspended_scripts (a String
    // column) give a string. Normalising here keeps one type on the row, so callers can send
    // it to an API or compare it without every site remembering to coerce.
    const rawProcessId = notification.processId || notification.processID;
    const stableProcessId = rawProcessId ? String(rawProcessId) : '';
    const historyId =
        notification.id !== undefined && notification.id !== null
            ? String(notification.id)
            : '';

    const normalizeKeyPart = (value) => {
        if (value === undefined || value === null) return '';
        return String(value).trim().toLowerCase();
    };

    const normalizeOutputFiles = (raw) => {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw.filter(Boolean);

        if (typeof raw === 'string') {
            const trimmed = raw.trim();
            if (!trimmed || trimmed === '[]') return [];

            if (
                (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
                (trimmed.startsWith('"[') && trimmed.endsWith(']"'))
            ) {
                try {
                    const parsed = JSON.parse(trimmed.replace(/^"|"$/g, ''));
                    if (Array.isArray(parsed)) {
                        return parsed.filter(Boolean);
                    }
                } catch (e) {
                    // fall through
                }
            }

            return [trimmed];
        }

        return [raw];
    };

    const formattedCompletionTimestamp = formatIsoTimestamp(notification.timeStamp);
    const formattedCreationTimestamp = formatIsoTimestamp(notification.creationTime);

    // timeStamp is treated as the "last update" time in live socket updates.
    // In history rows, it represents completion time (since we only store final status).
    const formattedLastUpdateTimestamp = formattedCompletionTimestamp;

    // Left table should show creation time only. If legacy rows have NULL creation_time,
    // we keep it empty instead of falling back to completion time.
    const formattedTimestamp = formattedCreationTimestamp;
    const { date: datePart, time: timePart } = formattedTimestamp
        ? getDisplayDateTimeParts(formattedTimestamp)
        : { date: '', time: '' };
    const displayTimestamp = formattedTimestamp ? `${datePart}, ${timePart}` : '-';

    const { date: creationDatePart, time: creationTimePart } = formattedCreationTimestamp
        ? getDisplayDateTimeParts(formattedCreationTimestamp)
        : { date: '', time: '' };
    const displayCreationTime = formattedCreationTimestamp
        ? `${creationDatePart}, ${creationTimePart}`
        : '';

    const { date: completionDatePart, time: completionTimePart } = formattedCompletionTimestamp
        ? getDisplayDateTimeParts(formattedCompletionTimestamp)
        : { date: '', time: '' };
    // 'paused'/'waiting' belong here alongside the in-flight states: a parked run has not
    // completed, so it must not show a completion time.
    const isUnfinishedProgress =
        notification.progress &&
        ([
            TaskProgressType.PROCESSING,
            TaskProgressType.STARTED,
            TaskProgressType.CANCELLING,
        ].includes(notification.progress) ||
            ['paused', 'waiting'].includes(
                String(notification.progress).toLowerCase()
            ));

    const displayCompletionTime =
        isUnfinishedProgress
            ? ''
            : formattedCompletionTimestamp
            ? `${completionDatePart}, ${completionTimePart}`
            : '';

    const { date: lastUpdateDatePart, time: lastUpdateTimePart } = formattedLastUpdateTimestamp
        ? getDisplayDateTimeParts(formattedLastUpdateTimestamp)
        : { date: '', time: '' };
    const displayLastUpdateTime = formattedLastUpdateTimestamp
        ? `${lastUpdateDatePart}, ${lastUpdateTimePart}`
        : '';

    let timestampMs = 0;
    if (formattedTimestamp) {
        const parseTarget = formattedTimestamp.includes('T')
            ? formattedTimestamp
            : formattedTimestamp.replace(' ', 'T');
        const parsed = Date.parse(parseTarget);
        if (!Number.isNaN(parsed)) {
            timestampMs = parsed;
        }
    }

    let lastUpdateMs = 0;
    if (formattedLastUpdateTimestamp) {
        const parseTarget = formattedLastUpdateTimestamp.includes('T')
            ? formattedLastUpdateTimestamp
            : formattedLastUpdateTimestamp.replace(' ', 'T');
        const parsed = Date.parse(parseTarget);
        if (!Number.isNaN(parsed)) {
            lastUpdateMs = parsed;
        }
    }

    const status = mapProgressToStatus(notification.progress);
    const parsedOutput = parseOutputText(notification.output_text);
    const outputFiles = normalizeOutputFiles(notification.output_files);

    const normalizeAddedFileNames = (raw) => {
        if (!raw) return [];

        if (typeof raw === 'string') {
            const trimmed = raw.trim();
            if (trimmed === '[]' || trimmed === '') return [];

            if (
                (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
                (trimmed.startsWith('"[') && trimmed.endsWith(']"'))
            ) {
                try {
                    const parsed = JSON.parse(trimmed.replace(/^"|"$/g, ''));
                    if (Array.isArray(parsed)) {
                        return parsed.filter(Boolean);
                    }
                } catch (e) {
                    // fall through
                }
            }

            return [trimmed];
        }

        if (Array.isArray(raw)) return raw.filter(Boolean);

        return [raw];
    };

    const addedFileNames = normalizeAddedFileNames(notification.addedFileNames);

    const syntheticMergeKeys = Array.from(
        new Set(
            [notification.repoId, notification.repoName]
                .map((value) => normalizeKeyPart(value))
                .filter(Boolean)
                .map(
                    (repoKey) =>
                        `${normalizeKeyPart(notification.userMail)}-${repoKey}-${normalizeKeyPart(
                            notification.process
                        )}`
                )
        )
    );
    const stableSyntheticId = syntheticMergeKeys[0] || '';

    const mergeKey = stableProcessId || historyId || stableSyntheticId;

    // `id` must be unique per row for AntD Table to render/filter correctly.
    // For history rows, backend provides a unique numeric `notification.id`.
    // For live rows, we fall back to mergeKey.
    const uniqueRowId = historyId || mergeKey;

    return {
        id: uniqueRowId,
        mergeKey,
        historyId,
        processId: stableProcessId || '',
        syntheticMergeKeys,
        isLive: Boolean(notification.isLive),
        repoId: notification.repoId,
        repoOrAgentName: notification.repoName,
        taskType: notification.process,
        status,
        progress: notification.progress,
        userId: notification.userMail,
        userType: 'User',
        timestamp: displayTimestamp,
        timestampMs,
        creationTime: displayCreationTime,
        completionTime: displayCompletionTime,
        lastUpdateTime: displayLastUpdateTime,
        lastUpdateMs,
        percentageCompletion: notification.percentageCompletion,
        message: notification.message,
        parsedOutput,
        outputFiles,
        templateName: notification.templateName,
        addedFileNames,
    };
};
